// src/components/pages/CatalogItems.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  useTheme,
  Skeleton,
  Button,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { bottles, detailsMap } from '../../data/Bottles';
import ThirdLoader from '../loaders/ThirdLoader';

export default function ShopHarvestItems({ harvestYear }) {
  const theme = useTheme();
  const navigate = useNavigate();

  // loading skeleton
  const [loading, setLoading] = useState(true);
  // nav loader
  const [navLoading, setNavLoading] = useState(false);

  // filter state
  const [yearFilter, setYearFilter] = useState(harvestYear || 'all');
  const [volumeFilter, setVolumeFilter] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (harvestYear) {
      setYearFilter(harvestYear);
    }
  }, [harvestYear]);
  

  const handleNavigate = (id) => {
    setNavLoading(true);
    setTimeout(() => {
      navigate(`/products/${id}`);
    }, 2000);
  };

  // Filter the bottles array based on our filters
  const filteredBottles = useMemo(() => {
    return bottles.filter((b) => {
      // Year filter
 // Year filter
      // only show the selected harvest
      if (yearFilter !== 'all' && String(b.harvestYear) !== yearFilter) {
        return false;
      }      // Volume filter
      if (volumeFilter !== 'all' && b.volume !== volumeFilter) return false;
      // Price filter
      const priceNum = parseFloat(b.price.replace(/[^0-9.]/g, ''));
      if (minPrice && priceNum < parseFloat(minPrice)) return false;
      if (maxPrice && priceNum > parseFloat(maxPrice)) return false;
      return true;
    });
  }, [yearFilter, volumeFilter, minPrice, maxPrice]);

  const cardVariants = {
    hidden:   { opacity: 0, y:  80 },
    visible:  { opacity: 1, y:   0, transition: { duration: 0.8, ease: 'easeOut' } },
    hover:    { scale:   1.15, y: -30, transition: { duration: 0.3 } },
  };

  const badgeVariants = {
    hidden: { scale: 1, rotate: 0 },
    hover:  { scale: 1.4, rotate: 360, transition: { duration: 0.6, ease: 'easeInOut' } },
  };

  return (
    <>
      {/* Navigation loader overlay */}
      <AnimatePresence>
        {navLoading && <ThirdLoader key="third-loader" />}
      </AnimatePresence>

      <Container
        id="catalog-items"
        component="section"
        sx={{
          backgroundColor: theme.palette.background.default,
          pt: { xs: 4, md: 6 },
          pb: { xs: 10, md: 16 },
        }}
      >
       {/* FILTER BAR */}
<Box
  sx={{
    mb: { xs: 6, md: 10 },
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: { xs: 2, md: 4 },
    borderBottom: theme => `1px solid ${theme.palette.divider}`,
    pb: 2,
  }}
>
  <FormControl
    variant="standard"
    size="small"
    sx={{ minWidth: 140 }}
  >
    <InputLabel sx={{ fontSize: '0.9rem' }}>Year</InputLabel>
    <Select
      value={yearFilter}
      onChange={e => setYearFilter(e.target.value)}
      sx={{
        fontSize: '0.95rem',
        '&:before, &:after': { borderBottomColor: 'currentColor' },
      }}
    >
      <MenuItem value="all">All Years</MenuItem>
      <MenuItem value="2024">2024</MenuItem>
      <MenuItem value="2025">2025</MenuItem>
    </Select>
  </FormControl>

  <FormControl
    variant="standard"
    size="small"
    sx={{ minWidth: 140 }}
  >
    <InputLabel sx={{ fontSize: '0.9rem' }}>Volume</InputLabel>
    <Select
      value={volumeFilter}
      onChange={e => setVolumeFilter(e.target.value)}
      sx={{
        fontSize: '0.95rem',
        '&:before, &:after': { borderBottomColor: 'currentColor' },
      }}
    >
      <MenuItem value="all">All Volumes</MenuItem>
      {[...new Set(bottles.map(b => b.volume))].map(vol => (
        <MenuItem key={vol} value={vol}>{vol}</MenuItem>
      ))}
    </Select>
  </FormControl>

  <Button
    variant="text"
    onClick={() => {
      setYearFilter('all');
      setVolumeFilter('all');
    }}
    sx={{
      fontSize: '0.9rem',
      textTransform: 'none',
      color: theme => theme.palette.text.secondary,
      '&:hover': { color: theme => theme.palette.text.primary },
    }}
  >
    Clear Filters
  </Button>
</Box>


        {/* optional overall scale if you want everything just a hair smaller */}
        <Box
          sx={{
            mx: 'auto',
            pt: 10,
            transform: 'scale(0.9)',
            transformOrigin: 'top center',
          }}
        >
          {/* GRID */}
          <Box
                      sx={{
                        display: 'grid',
                        gap: { xs: 12, md: 8 },
                        justifyItems: { xs: 'center', md: 'start' },
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2,1fr)',
                          md: 'repeat(4,1fr)',
                        },
                      }}
                    >
            {(loading
              ? Array(filteredBottles.length).fill({})
              : filteredBottles
            ).map((b, i) => (
              <Box
                key={i}
                component={motion.div}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                whileHover="hover"
                viewport={{ once: true, amount: 0.4 }}
                sx={{
                  width: 300,
                  perspective: 1400,
                  '&:hover .infoPanel': {
                    opacity: 1,
                    transform: 'translate(-50%, 0)',
                    pointerEvents: 'auto',
                  },
                  '&:hover .staticTitle': {
                    opacity: 0,
                  },
                }}
              >
                {/* Card Container */}
                <Box
                  sx={{
                    position: 'relative',
                    width: 260,
                    height: 380,
                    mx: 'auto',
                    mt: 10,
                    borderRadius: 3,
                    backgroundColor: theme.palette.grey[50],
                    border: '1px solid #EEF2F5',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    overflow: 'visible',
                  }}
                >
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={300}
                      animation="wave"
                      sx={{ borderRadius: 3 }}
                    />
                  ) : (
                    <>
                      {/* Product Image */}
                      <Box
                        component="img"
                        src={b.img}
                        alt={b.name}
                        sx={{
                          position: 'absolute',
                          top: -140,               // pulled up a bit more
                          left: '50%',
                          transform: 'translateX(-50%)',
                          maxHeight: 550,
                          objectFit: 'contain',
                          width: 'auto',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleNavigate(b.id)}
                      />

                      {/* Award Badge */}
                      {b.awards && (
                        <Box
                          component={motion.div}
                          variants={badgeVariants}
                          initial="hidden"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.secondary.main,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            textAlign: 'center',
                            lineHeight: 1.2,
                            pointerEvents: 'none',
                          }}
                        >
                          {b.awards}
                        </Box>
                      )}
                    </>
                  )}

                  {/* Info Panel on hover */}
                  {!loading && (
                    <Box
                      className="infoPanel"
                      sx={{
                        position: 'absolute',
                        bottom: -140,
                        left: '50%',
                        transform: 'translate(-50%, 100%)',
                        width: 280,
                        backgroundColor: 'rgba(255,255,255,0.98)',
                        borderRadius: 2,
                        boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                        opacity: 0,
                        transition: 'opacity 0.5s ease, transform 0.5s ease',
                        zIndex: 10,
                        pointerEvents: 'none',
                      }}
                    >
                      <Box sx={{ px: 2.5, py: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontFamily: '"Playfair Display", serif',
                            fontWeight: 600,
                            mb: 1,
                            textAlign: 'center',
                            fontSize: '1.15rem',
                          }}
                        >
                          {b.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            textAlign: 'center',
                            fontStyle: 'italic',
                            fontSize: '0.85rem',
                          }}
                        >
                          {b.volume}
                        </Typography>
                        <List disablePadding>
                          {detailsMap[b.id]?.map((item) => (
                            <ListItem
                              key={item.label}
                              sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}
                            >
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: item.color,
                                  mr: 1.2,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.85rem', color: theme.palette.text.primary }}
                              >
                                {item.label}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{
                            mt: 2,
                            fontSize: '0.95rem',
                            py: 1.3,
                            backgroundColor: theme.palette.common.black,
                            color: theme.palette.common.white,
                            '&:hover': { backgroundColor: theme.palette.grey[900] },
                          }}
                          onClick={() => handleNavigate(b.id)}
                        >
                          Check Availability
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Static Title */}
                {!loading && (
                  <Typography
                    className="staticTitle"
                    variant="subtitle1"
                    sx={{
                      mt: 6,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      textAlign: 'center',
                      cursor: 'default',
                      transition: 'opacity 0.3s ease',
                      fontSize: '1.05rem',
                    }}
                  >
                    {b.name}
                  </Typography>
                )}

                {/* PRICE */}
                {!loading && (
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{ fontWeight: 600, mt: 0.5, mb: 1 }}
                  >
                    {b.price}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </>
  );
}
